import React from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { IphoneIconPreviewCanvas } from "../IphoneIconPreviewCanvas";
import { ImageCropDialog } from "../../new-editor/elements/ImageCropDialog";
import { ImageOff, ImageUp, Trash2, Upload } from "lucide-react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToastAlert } from "../../app/ToastAlert/ToastAlertProvider";
import WorkspacesService from "../../../services/WorkspacesService";
import {
  useCurrentBrand,
  useCurrentWorkspace,
} from "../../../hooks/useCurrentWorkspace";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import {
  CURRENT_USER_QUERY_KEY,
  CURRENT_WORKSPACE_QUERY_KEY,
} from "../../../wrappers/current-user/CurrentUserWrapper";
import StorageService from "../../../services/StorageService";
import {
  AssetType,
  BrandedAppStatus,
  GetWorkspaceDto,
  UserInfoDto,
} from "@growth-machine-llc/stridist-api-client";
import { uploadFile } from "../../../utils/file";
import { makeStyles } from "@mui/styles";
import { IphoneSplashPreviewCanvas } from "../IphoneSplashPreviewCanvas";
import { SplashBackgroundColorPicker } from "../SplashBackgroundColorPicker";
import { SharablePreviewCanvas } from "./SharablePreviewCanvas";
import demoWorkoutSharing from "../../../assets/workout-sharing-demo.png";

import appIconPlaceholder from "../../../assets/placeholder-appicon.png";
import bannerPlaceholder from "../../../assets/banner-placeholder.png";
import { ImageUploadButtons } from "./ImageUploadButtons";

const useStyles = makeStyles((theme) => ({
  header: {
    marginBottom: theme.spacing(2.5),
  },

  titleBox: {
    display: "flex",
    alignItems: "center",
    gap: 0,
    flexDirection: "column",
    marginBottom: theme.spacing(2),

    [theme.breakpoints.up("sm")]: {
      flexDirection: "row",
      gap: theme.spacing(2),
      marginBottom: 0,
    },
  },

  text: {
    fontSize: 16,
    fontWeight: 500,
    color: theme.palette.text.primary,
  },

  title: {
    fontSize: 32,
    fontWeight: "bold",
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
  },

  statusBox: {
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    padding: theme.spacing(0.5, 2),
    flexShrink: 0,
    marginBottom: 4,
  },

  status: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
  },

  centerColumn: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-around",
    gap: theme.spacing(5),
  },

  uploadContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(2),

    [theme.breakpoints.up("md")]: {
      flexDirection: "row",
      gap: theme.spacing(4),
      alignItems: "flex-start",
      justifyContent: "space-between",
    },
  },

  uploadButton: {
    display: "flex",
    gap: theme.spacing(1),
    maxWidth: theme.breakpoints.values.sm,
  },

  deleteButton: {
    width: "fit-content",
  },

  uploadSection: {
    width: "100%",
    maxWidth: 500,
  },

  submitButton: {
    display: "flex",
    gap: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(4),
    height: theme.spacing(8),
    fontSize: 20,
    fontWeight: 700,
    lineHeight: 1,
    padding: theme.spacing(1, 2),

    "&:hover": {
      backgroundColor: theme.palette.primary.light,
    },
  },

  uploadPreview: {
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    gap: theme.spacing(2),
    alignItems: "center",
    width: "100%",
    // maxWidth: theme.spacing(44),
    marginTop: theme.spacing(2),

    [theme.breakpoints.up("md")]: {
      flexDirection: "row",
    },
  },

  sharablePreview: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    paddingTop: theme.spacing(-2),
    width: theme.spacing(44),
    maxWidth: "100%",
  },

  a: {
    color: theme.palette.primary.main,
    cursor: "pointer",

    "&:hover": {
      color: theme.palette.primary.light,
    },
  },
}));

interface ConfigurationContentProps {
  contactSupport: () => void;
}

type ImageObject = {
  url: string;
  filename: string;
};

export function ConfigurationContent(props: ConfigurationContentProps) {
  const s = useStyles();

  const { contactSupport } = props;

  const { showToastAlert } = useToastAlert();
  const queryClient = useQueryClient();

  const { id: workspaceId, logoUrl: workspaceLogo } = useCurrentWorkspace();

  const [appLogoCropperOpen, setAppLogoCropperOpen] = React.useState(false);
  const [sharableCropperOpen, setSharableCropperOpen] = React.useState(false);
  const [splashCropperOpen, setSplashCropperOpen] = React.useState(false);

  const {
    appName,
    brandedAppLogo,
    brandedAppBanner,
    brandedAppSplashScreen,
    brandedAppSplashBackgroundColor,
  } = useCurrentBrand();

  const getFileNameFromUrl = (url: string) => {
    if (!url) return null;
    const chunks = url.split("/");
    return chunks[chunks.length - 1];
  };

  const [logoSrc, setLogoSrc] = React.useState<ImageObject>({
    url: brandedAppLogo,
    filename: getFileNameFromUrl(brandedAppLogo),
  });
  const [sharableLogoSrc, setSharableLogoSrc] = React.useState<ImageObject>({
    url: brandedAppBanner || workspaceLogo,
    filename: getFileNameFromUrl(brandedAppBanner || workspaceLogo),
  });
  const [splashScreenSrc, setSplashScreenSrc] = React.useState<ImageObject>({
    url: brandedAppSplashScreen || workspaceLogo,
    filename: getFileNameFromUrl(brandedAppSplashScreen || workspaceLogo),
  });
  const [splashBackgroundColor, setSplashBackgroundColor] =
    React.useState<string>(brandedAppSplashBackgroundColor || "#FFFFFF");
  const [nameInput, setNameInput] = React.useState<string>(appName || "");
  const [nameError, setNameError] = React.useState<string | null>(null);
  const [loadingState, setLoadingState] = React.useState({
    icon: false,
    splash: false,
    banner: false,
  });

  const { mutate: submitApplication } = useMutation({
    mutationFn: WorkspacesService.submitBrandedAppRequest,
  });

  const validateName = (name: string) => {
    if (name.length < 3) {
      return "Name must be at least 3 characters long";
    } else if (name.length > 30) {
      return "Name must be less than 30 characters long";
    }
    return null;
  };

  const { mutate: createUrl } = useMutation({
    mutationFn: StorageService.createSignedUrl,
  });

  const handleImageSave = (
    onSuccess: ({ url, filename }: { url: string; filename: string }) => void,
    imageType: "icon" | "splash" | "banner",
  ) => {
    return (logo: File) => {
      setLoadingState((state) => ({ ...state, [imageType]: true }));
      createUrl(
        {
          refId: workspaceId,
          refType: "Workspace",
          file: logo.name,
          assetType: AssetType.BRANDED_APP,
        },
        {
          onSuccess: (urlVm) => {
            const url = urlVm.url;
            uploadFile(url, logo)
              .then(() => {
                const urlInstance = new URL(
                  `https:/${url.substr(url.indexOf("/", 8))}`,
                );
                urlInstance.search = "";
                const uploadUrl = String(urlInstance);
                onSuccess({ url: uploadUrl, filename: logo.name });
                setLoadingState((state) => ({ ...state, [imageType]: false }));
              })
              .catch((error) => {
                console.error("Error uploading logo:", error);
              });
          },
        },
      );
    };
  };

  const handleSubmitApplication = () => {
    if (!nameInput || !logoSrc) {
      showToastAlert("error", {
        message: "Please provide both app name and icon before submitting.",
      });
      return;
    }
    const nameError = validateName(nameInput);
    if (nameError) {
      showToastAlert("error", {
        message: nameError,
      });
      setNameError(nameError);
      return;
    }
    submitApplication(
      {
        workspaceId,
        name: nameInput,
        logoUrl: logoSrc.url,
        bannerUrl: sharableLogoSrc.url,
        splashScreenUrl: splashScreenSrc.url,
        splashBackgroundColor: splashBackgroundColor,
      },
      {
        onSuccess: () => {
          showToastAlert("success", {
            message:
              "Branded app request submitted successfully! Our team will review it shortly.",
          });
          queryClient.setQueriesData(
            {
              queryKey: [CURRENT_USER_QUERY_KEY],
            },
            (oldData: UserInfoDto) => {
              if (!oldData?.workspace?.brandedApp) {
                return oldData;
              }

              return UserInfoDto.fromJS({
                ...oldData,
                workspace: {
                  ...oldData.workspace,
                  brandedApp: {
                    ...oldData.workspace.brandedApp,
                    status: BrandedAppStatus.PendingReview,
                  },
                },
              });
            },
          );
        },
        onError: () => {
          showToastAlert("error", {
            message:
              "Failed to submit branded app request. Please try again later.",
          });
        },
      },
    );
  };

  return (
    <>
      <Box className={s.header}>
        <Box className={s.titleBox}>
          <Typography className={s.title}>Branded App</Typography>
          <Box className={s.statusBox}>
            <Typography className={s.status}>configuration</Typography>
          </Box>
        </Box>
        <Typography className={s.text}>
          Configure your custom branded app for your clients. Use this page to
          set up your preferred app name, icon, and branding.
        </Typography>
        <Typography className={s.text} sx={{ marginTop: 2 }}>
          In the meantime, you can continue using the standard Stridist app for
          your clients. If you have any questions or need assistance, please
          contact us via{" "}
          <span
            {...(contactSupport && { onClick: contactSupport, className: s.a })}
          >
            chat
          </span>{" "}
          with Branded Apps Manager.
        </Typography>
        <Typography className={s.text} sx={{ marginTop: 2 }}>
          No effort will be required to migrate your existing or future clients
          — everything will work seamlessly.
        </Typography>
      </Box>
      <Box className={s.centerColumn}>
        <Box className={s.uploadContainer}>
          <Box className={s.uploadSection}>
            <Typography variant="h6" marginBottom={1}>
              App Appearance
            </Typography>
            <Typography className={s.text} sx={{ marginBottom: 2 }}>
              Configure your app's name and icon that will be visible to clients
              on their device's home screen.
            </Typography>
            <TextField
              error={!!nameError}
              helperText={nameError}
              value={nameInput}
              fullWidth
              onChange={(e) => {
                setNameInput(e.target.value);
                nameError && setNameError(validateName(e.target.value));
              }}
              sx={{ marginBottom: 2 }}
              placeholder="My App Name"
            />
            <ImageUploadButtons
              fileName={logoSrc.filename}
              onUpload={() => setAppLogoCropperOpen(true)}
              onDelete={() => setLogoSrc({ url: null, filename: null })}
              uploadLabel="Icon"
              loading={loadingState.icon}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Upload a square image (1:1 ratio) that will be used as your app
              icon. Minimum size: 1024x1024 pixels.
            </Typography>
          </Box>
          <Box className={s.uploadPreview}>
            <Box className={s.sharablePreview}>
              <IphoneIconPreviewCanvas
                iconUrl={logoSrc.url}
                appName={nameInput}
              />
            </Box>
          </Box>
        </Box>
        <Box className={s.uploadContainer}>
          <Box className={s.uploadSection}>
            <Typography variant="h6" marginBottom={1}>
              Splash screen
            </Typography>
            <Typography className={s.text} sx={{ marginBottom: 2 }}>
              Configure your app's splash screen that will be visible to clients
              when they launch the app.
            </Typography>
            <ImageUploadButtons
              fileName={splashScreenSrc.filename}
              onUpload={() => setSplashCropperOpen(true)}
              onDelete={() => setSplashScreenSrc({ url: null, filename: null })}
              uploadLabel="Splash Screen"
              loading={loadingState.splash}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Upload a square image (1:1 ratio) that will be used as your app
              splash screen. Minimum size: 2732x2732 pixels.
            </Typography>
            <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
              Background color
            </Typography>
            <SplashBackgroundColorPicker
              value={splashBackgroundColor}
              onChange={setSplashBackgroundColor}
            />
          </Box>
          <Box className={s.uploadPreview}>
            <IphoneSplashPreviewCanvas
              splashUrl={splashScreenSrc.url || appIconPlaceholder}
              backgroundColor={splashBackgroundColor}
            />
          </Box>
        </Box>
        <Box className={s.uploadContainer}>
          <Box className={s.uploadSection}>
            <Typography variant="h6" marginBottom={1}>
              Brand Watermark
            </Typography>
            <Typography className={s.text} sx={{ marginBottom: 2 }}>
              This watermark will be used in shareables, like training summaries
              or progress photos.
            </Typography>
            <ImageUploadButtons
              fileName={sharableLogoSrc.filename}
              onUpload={() => setSharableCropperOpen(true)}
              onDelete={() => setSharableLogoSrc({ url: null, filename: null })}
              uploadLabel="Watermark"
              loading={loadingState.banner}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Upload a wide watermark image that will appear at the top of
              shared content. Minimum height: 50 pixels.
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              width: "100%",
            }}
          >
            <Box className={s.uploadPreview}>
              <Box className={s.sharablePreview}>
                <SharablePreviewCanvas
                  brandedAppBanner={sharableLogoSrc.url || bannerPlaceholder}
                  backgroundUrl={demoWorkoutSharing}
                  title="Workout Sharing Preview"
                />
              </Box>
            </Box>
            <Box className={s.uploadPreview}>
              <Box className={s.sharablePreview}>
                <SharablePreviewCanvas
                  brandedAppBanner={sharableLogoSrc.url || bannerPlaceholder}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <Button
        className={s.submitButton}
        variant="contained"
        size="large"
        onClick={handleSubmitApplication}
        fullWidth
      >
        Submit Application <Upload size={30} />
      </Button>
      <ImageCropDialog
        imageRestrictions={{ aspectRatio: 1 / 1, minWidth: 1024 }}
        open={appLogoCropperOpen}
        onClose={() => setAppLogoCropperOpen(false)}
        onSubmit={handleImageSave(setLogoSrc, "icon")}
      />
      <ImageCropDialog
        imageRestrictions={{
          aspectRatio: { maximum: 10 / 1, minimum: 1 },
          minHeight: 50,
        }}
        open={sharableCropperOpen}
        onClose={() => setSharableCropperOpen(false)}
        onSubmit={handleImageSave(setSharableLogoSrc, "banner")}
      />
      <ImageCropDialog
        imageRestrictions={{ aspectRatio: 1 / 1, minWidth: 2732 }}
        open={splashCropperOpen}
        onClose={() => setSplashCropperOpen(false)}
        onSubmit={handleImageSave(setSplashScreenSrc, "splash")}
      />
    </>
  );
}
